/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getFeatureFlag } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { addFavoriteApi, deleteFavorite, getFavorite } from '../../../../../apis/product-apis/favorites';
import { getContactId } from '../../../../../state/ducks/App/ducks/Common/Common-Selectors';
import { getBrand } from '../../../../pages/Account/state/ducks/Common/Common-Selectors';
import { getFavoriteData, getPageType } from '../../../../../state/ducks/App/App-Selectors';
import { setFavoriteData } from '../../../../../state/ducks/App/App-Actions';
import FavoriteSnackbar from './FavoriteSnackBar';
import GraphqlPortalLoader from '../GraphqlPortalLoader';

const useStyles = makeStyles((theme) => ({
    root: {
        background: 'none',
        border: 'none',
        padding: '16px',
        cursor: 'pointer',
        display: 'flex',
        position: 'absolute',
        width: 'auto',
        height: 'auto',
        top: 0,
        justifyContent: 'flex-end',
        right: 0,
    },
}));
const FavoritesUI = ({ partNumber }) => {
    const contactId = useSelector((state) => getContactId(state));
    const isFavoriteEnabled  = useSelector(getFeatureFlag('is-favorites-enabled'));
    if (!isFavoriteEnabled || !contactId) {
        return null;
    }
    const [loading, setLoading] = useState(false);
    const pageType = useSelector(getPageType);
    const favoriteData = useSelector(getFavoriteData);
    const dispatch = useDispatch();
    const classes = useStyles();
    const currentBrand = useSelector((state) =>  getBrand(state));
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [favoriteProducts, setFavoriteProducts] = useState({});
    const fetchFavorites = async () => {
        setLoading(true);
        try {
            const data = await getFavorite(contactId);
            const addFavoriteData = data?.favoritesList?.[0] || { favorites: [] };
            setFavoriteProducts(addFavoriteData);
            dispatch(setFavoriteData(addFavoriteData));
        } catch (error) {
            console.error('Error fetching favorites:', error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (favoriteData?.favorites?.length > 0) {
            setFavoriteProducts(favoriteData);
        }
    }, [favoriteData]);
    useEffect(() => {
        const favoriteFlag = JSON.parse(sessionStorage.getItem('favorite') || 'false');
        if (!favoriteFlag && contactId) {
            fetchFavorites();
            sessionStorage.setItem('favorite', true);
        }
    }, [contactId]);
    const handleToggle = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        const isSelectedFavorite = favoriteProducts?.favorites?.some((item) => item?.baseCode === partNumber) || false;
        setLoading(true);
        try {
            if (!isSelectedFavorite) {
                // Add favorite
                const payload = {
                    baseCode: partNumber,
                    sourceBrand: currentBrand?.id,
                    sourceUser: 'pwa',
                };
                const addFavoriteData = await addFavoriteApi(contactId, payload);
                setFavoriteProducts(addFavoriteData);
                if (pageType === 'pdp' || pageType === 'product') {
                    setShowSnackbar(true);
                }
            } else {
                // Delete favorite
                const checkPartNumber = favoriteProducts?.favorites?.filter((item) => item?.baseCode === partNumber);
                const payload = {
                    favoritesToDelete: [
                        {
                            favoritesListId: favoriteProducts?.favoritesListId,
                            favoriteId: checkPartNumber?.[0]?.favoriteId,
                        },
                    ],
                };
                await deleteFavorite(contactId, payload);
                const updateData = {
                    ...favoriteProducts,
                    favorites: favoriteProducts?.favorites?.filter((item) => item?.baseCode !== partNumber),
                };
                setFavoriteProducts(updateData);
            }
        } catch (error) {
            console.error('Error in favorite toggle:', error);
        } finally {
            setLoading(false);
        }
    };
    const filledFavorite = favoriteProducts?.favorites?.some((item) => item?.baseCode === partNumber) || false;
    const ariaLabel = filledFavorite ? 'Remove from favorites' : 'Add to favorites';
    return (
        <>
            {loading && <GraphqlPortalLoader />}
            <button
                type="button"
                onClick={(e) => handleToggle(e)}
                aria-pressed={filledFavorite}
                aria-label={ariaLabel}
                className={classes.root}
                tabIndex="0"
            >
                {filledFavorite ? (
                    <svg width="30" height="26" viewBox="0 0 30 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M18.2243 0.636223C19.2576 0.216191 20.3651 0 21.4835 0C22.6019 0 23.7094 0.216191 24.7426 0.636223C25.7757 1.05618 26.7143 1.67167 27.5049 2.44755C28.2957 3.22318 28.9235 4.14447 29.3515 5.15799C29.7796 6.1717 30 7.25823 30 8.35552C30 9.45281 29.7796 10.5393 29.3515 11.553C28.9234 12.5667 28.296 13.4876 27.5051 14.2633L15.9259 25.6236C15.4143 26.1255 14.585 26.1255 14.0734 25.6236L2.49422 14.2633C0.897199 12.6964 0 10.5714 0 8.35552C0 6.13968 0.897199 4.0146 2.49422 2.44776C4.09125 0.880926 6.25728 0.000687239 8.51582 0.000687239C10.7744 0.000687239 12.9404 0.880926 14.5374 2.44776L14.9997 2.90127L15.4617 2.44798C16.2523 1.672 17.1912 1.05622 18.2243 0.636223ZM21.4835 2.57021C20.7092 2.57021 19.9425 2.71988 19.2272 3.01067C18.5118 3.30146 17.8619 3.72767 17.3145 4.26496L15.9259 5.62738C15.4143 6.12925 14.585 6.12925 14.0734 5.62738L12.685 4.26517C11.5792 3.18035 10.0796 2.5709 8.51582 2.5709C6.95208 2.5709 5.45238 3.18035 4.34665 4.26517C3.24092 5.35 2.61973 6.82134 2.61973 8.35552C2.61973 9.8897 3.24092 11.361 4.34665 12.4459L14.9997 22.8975L25.6526 12.4459C26.2003 11.9088 26.6349 11.271 26.9313 10.5692C27.2277 9.8674 27.3803 9.11518 27.3803 8.35552C27.3803 7.59586 27.2277 6.84365 26.9313 6.14185C26.6349 5.44005 26.2005 4.80242 25.6529 4.26539C25.1055 3.7281 24.4551 3.30146 23.7398 3.01067C23.0245 2.71988 22.2578 2.57021 21.4835 2.57021Z" fill="#185357" />
                        <path d="M19.2272 3.01067C19.9425 2.71988 20.7092 2.57021 21.4835 2.57021C22.2578 2.57021 23.0245 2.71988 23.7398 3.01067C24.4551 3.30146 25.1055 3.7281 25.6529 4.26539C26.2005 4.80242 26.6349 5.44005 26.9313 6.14185C27.2277 6.84365 27.3803 7.59586 27.3803 8.35552C27.3803 9.11518 27.2277 9.8674 26.9313 10.5692C26.6349 11.271 26.2003 11.9088 25.6526 12.4459L14.9997 22.8975L4.34665 12.4459C3.24092 11.361 2.61973 9.8897 2.61973 8.35552C2.61973 6.82134 3.24092 5.35 4.34665 4.26517C5.45238 3.18035 6.95208 2.5709 8.51582 2.5709C10.0796 2.5709 11.5792 3.18035 12.685 4.26517L14.0734 5.62738C14.585 6.12925 15.4143 6.12925 15.9259 5.62738L17.3145 4.26496C17.8619 3.72767 18.5118 3.30146 19.2272 3.01067Z" fill="#BDDFCE" />
                    </svg>
                ) : (
                    <svg width="30" height="26" viewBox="0 0 34 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="Vector ">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M20.6543 0.734103C21.8253 0.249451 23.0804 0 24.3479 0C25.6155 0 26.8706 0.249451 28.0416 0.734103C29.2124 1.21867 30.2762 1.92885 31.1722 2.82409C32.0684 3.71905 32.7799 4.78208 33.265 5.95153C33.7503 7.12119 34 8.37489 34 9.64099C34 10.9071 33.7503 12.1608 33.265 13.3304C32.7799 14.5 32.0688 15.5626 31.1724 16.4576L18.0493 29.5657C17.4696 30.1448 16.5296 30.1448 15.9499 29.5657L2.82679 16.4576C1.01683 14.6497 0 12.1977 0 9.64099C0 7.08425 1.01683 4.63223 2.82679 2.82434C4.63675 1.01645 7.09159 0.000792968 9.65126 0.000792968C12.2109 0.000792968 14.6658 1.01645 16.4757 2.82434L16.9996 3.34762L17.5232 2.82459C18.4193 1.92923 19.4834 1.21871 20.6543 0.734103ZM24.3479 2.96562C23.4704 2.96562 22.6015 3.13832 21.7908 3.47385C20.9801 3.80938 20.2435 4.30116 19.6231 4.92111L18.0493 6.49313C17.4696 7.07221 16.5296 7.07221 15.9499 6.49313L14.3763 4.92135C13.1231 3.66963 11.4235 2.96642 9.65126 2.96642C7.87902 2.96642 6.17937 3.66963 4.92621 4.92135C3.67305 6.17308 2.96903 7.87078 2.96903 9.64099C2.96903 11.4112 3.67305 13.1089 4.92621 14.3606L16.9996 26.4202L29.073 14.3606C29.6937 13.741 30.1862 13.005 30.5222 12.1952C30.8581 11.3855 31.031 10.5175 31.031 9.64099C31.031 8.76446 30.8581 7.89652 30.5222 7.08675C30.1862 6.27698 29.6939 5.54125 29.0732 4.9216C28.4529 4.30165 27.7158 3.80938 26.9051 3.47385C26.0944 3.13832 25.2255 2.96562 24.3479 2.96562Z" fill="#185357" />
                            <path d="M21.7908 3.47385C22.6015 3.13832 23.4704 2.96562 24.3479 2.96562C25.2255 2.96562 26.0944 3.13832 26.9051 3.47385C27.7158 3.80938 28.4529 4.30165 29.0732 4.9216C29.6939 5.54125 30.1862 6.27698 30.5222 7.08675C30.8581 7.89652 31.031 8.76446 31.031 9.64099C31.031 10.5175 30.8581 11.3855 30.5222 12.1952C30.1862 13.005 29.6937 13.741 29.073 14.3606L16.9996 26.4202L4.92621 14.3606C3.67305 13.1089 2.96903 11.4112 2.96903 9.64099C2.96903 7.87078 3.67305 6.17308 4.92621 4.92135C6.17937 3.66963 7.87902 2.96642 9.65126 2.96642C11.4235 2.96642 13.1231 3.66963 14.3763 4.92135L15.9499 6.49313C16.5296 7.07221 17.4696 7.07221 18.0493 6.49313L19.6231 4.92111C20.2435 4.30116 20.9801 3.80938 21.7908 3.47385Z" fill="white" />
                        </g>
                    </svg>
                )}
            </button>
            {showSnackbar && (
                <FavoriteSnackbar
                    open={showSnackbar}
                    message="Added to your favorites"
                    onClose={() => setShowSnackbar(false)}
                />
            )}
        </>
    );
};
FavoritesUI.propTypes = {
    partNumber: string,
};
FavoritesUI.defaultProps = {
    partNumber: '',
};
export default React.memo(FavoritesUI);
